/**
 * Top-level orchestrator for a project's gen_3 epi-chat page.
 *
 * Composes the focused hooks (state, socket, stream reducer) into a single
 * bundle the page/layout components consume. One hook per concern keeps the
 * moving parts small and independently testable.
 *
 * Every event arriving on the shared socket is routed to the state slot of
 * the ``chatId`` it was tagged with, so a background chat's ongoing run
 * keeps accumulating even when a different tab is focused.
 */

import { nanoid } from '@reduxjs/toolkit';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { setIsWaitingForResponse } from '@verantos-dev/app-state-research-assistant';
import type { AgUiEvent } from '@verantos-dev/feature-agent-chat';

import { useEpiChatPersistence } from './useEpiChatPersistence';
import { selectSessionState, useEpiChatState } from './useEpiChatState';
import { useEpiChatStream } from './useEpiChatStream';
import { useEpiStudyAgentSocket, type EpiSocketStatus } from './useEpiStudyAgentSocket';
import { useGracefulConnectionStatus } from './useGracefulConnectionStatus';
import { useRunCompletionNotifier } from './useRunCompletionNotifier';
import { toWireAttachment } from '../api/attachmentWire';
import { usePendingChatCreation } from '../api/pendingChatCreation';
import type { ResourceInvalidationResolvers } from '../api/resourceQueryKeys';
import { type WireScope } from '../api/scopeWire';
import { toWireUserContext, type WireUserContext } from '../api/userContextWire';
import { OPTIMISTIC_SEND_RUN_ID } from '../state/epiChatSlice';
import type {
  EpiArtifact,
  EpiArtifactTab,
  EpiChatSendPayload,
  EpiMessage,
  EpiRunState,
} from '../types/epi-chat-types';

export interface UseEpiChatPageOptions {
  enabled?: boolean;
  /**
   * Current agent working scope (wire shape). Plumbed onto every outgoing
   * ``subscribe`` and ``user_message`` payload, and emitted as a
   * ``scope_changed`` event when the reference changes mid-session.
   *
   * The caller is expected to memoize this value (e.g. via the project-page
   * `toWireScope(useCurrentScope(...))` chain) so reference identity tracks
   * deep equality: the hook diffs by ref to decide whether to emit.
   */
  wireScope?: WireScope;
  /**
   * Project-anchored chat session id (Phase A of the multi-chat project
   * model). Drives the FE state-slot routing (``stateBySession[chatId]``)
   * AND rides the socket ``subscribe`` payload as `chat_id` so the BE
   * namespace resolves the row in `EPI_CHAT_SESSION` and dispatches S3
   * layout off that row's `project_id`.
   *
   * The BE expects `chat_id` (snake_case wire) — same convention as
   * `scope` on the subscribe payload (see `scopeWire.ts`).
   */
  chatId: string;
  /**
   * OR-6089: per-kind TanStack query-key resolvers used by the
   * `RESOURCE_UPDATED` stream-reducer case. Cohort + concept-group
   * keys are resolved inline by the stream hook; cross-feature kinds
   * (`analytical_variable`) need their resolver injected here so
   * `feature-epi-chat` doesn't import `feature-analytical-variables`
   * (would close a build cycle).
   *
   * The project-page mount that owns the chat (and already imports
   * both libs) is the natural injection point; legacy mounts can omit
   * and analytical variable invalidation drops to a no-op.
   */
  resourceResolvers?: ResourceInvalidationResolvers;
}

export interface UseEpiChatPageResult {
  status: EpiSocketStatus;
  messages: EpiMessage[];
  queue: EpiChatSendPayload[];
  runState: EpiRunState;
  error: string | null;
  /**
   * True while the backend is streaming a one-shot welcome message on a new
   * session. Consumers should block user input until it flips back to false.
   */
  initializing: boolean;
  /**
   * True between the FE firing ``subscribe`` and the backend acking
   * ``ready`` (or hydrating a snapshot). The chat pane shows a
   * "loading session…" skeleton while this is true and ``messages`` is
   * empty, so opening an existing session never looks blank-and-frozen.
   */
  subscribing: boolean;
  /**
   * Submit gate (OR-6139 AC 2). ``false`` until the backend's ``ready`` ack for
   * the current subscribe lands, so the composer cannot send a message before
   * the session's authoritative run state (in-flight or not) is known. Unlike
   * ``subscribing`` it is not cleared by a localStorage/replay hydrate.
   */
  subscribeAcked: boolean;
  artifacts: EpiArtifact[];
  activeArtifact: EpiArtifact | null;
  /**
   * Active tab on the artifact drawer. ``null`` means "use the default
   * for this artifact's kind" — callers don't need to resolve that;
   * {@link ArtifactDrawer} does it internally.
   */
  activeArtifactTab: EpiArtifactTab | null;
  activeSessionId: string | null;
  // Chat actions
  sendMessage: (payload: EpiChatSendPayload) => void;
  cancelRun: () => void;
  /** OR-6125: optimistically flip to the working state when a file upload is picked. */
  beginUpload: () => void;
  /** OR-6125: clear the optimistic upload working state if the POST never starts a turn. */
  endUpload: () => void;
  /** OR-6247 A2: optimistically flip to the working state when a concept group is Created (the BE resumes the chat server-side). */
  beginResume: () => void;
  /** OR-6247 A2: clear the optimistic resume working state if the Create POST never starts a turn. */
  endResume: () => void;
  dequeueMessage: (index: number) => void;
  selectArtifact: (id: string | null) => void;
  selectArtifactTab: (tab: EpiArtifactTab | null) => void;
  /**
   * Optimistic-update hook for the analytical variable sharing control. Successful
   * promotions get a server-truth round-trip via the next session push
   * anyway; this just lets the chip flip immediately so the epi sees
   * their action take effect without a perceptible lag.
   */
  applyArtifactScope: (id: string, scope: NonNullable<EpiArtifact['scope']>) => void;
  /**
   * Latest BE-broadcast task scope. ``null`` until the agent has emitted a
   * ``STATUS_UPDATE { kind: 'task_scope' }`` event (OR-6021 — renamed from
   * ``focus_scope``) on this session. Consumed by ``buildTaskScopeChip``
   * to render a display-only indicator near the composer; never used for
   * navigation, and not user-dismissable (it mirrors agent runtime
   * state — only the BE clears it via a subsequent task_scope emit).
   */
  remoteFocusScope: WireScope | null;
  /**
   * Set when the run's pod died mid-flight (multi-pod lease expiry; see
   * ``docs/DESIGN_MULTIPOD_RUN_LIFECYCLE.md`` D4). Drives the in-transcript
   * Retry card. ``null`` when no interrupted run is outstanding.
   */
  interruptedRun: { runId: string; retryable: boolean } | null;
  /**
   * Re-fire the user's last turn after an interrupted run. Resends the most
   * recent user-message text through the same path the composer uses, which
   * starts a fresh run and clears the interrupted-run notice.
   */
  retryInterruptedRun: () => void;
}

// Backoff schedule for re-subscribing after a transient (``SUBSCRIBE_UNAVAILABLE``)
// refusal. Length also caps the attempt budget — once exhausted the FE surfaces
// the terminal "no access" panel.
const SUBSCRIBE_RETRY_BACKOFF_MS = [500, 1000, 2000, 4000];

// OR-6247 A3: progress-stall watchdog window. While a run is streaming, if no
// event advances the durable cursor within this window the FE silently re-pulls
// durable state (re-subscribe from the cursor) rather than showing a frozen UI —
// covers a cross-pod delivery gap where work continues on another pod but its
// events aren't reaching this socket. Long enough that a slow-but-live step
// (e.g. a multi-minute SQL) doesn't trip it; short enough to feel responsive.
const RUN_PROGRESS_STALL_MS = 45_000;

// Sentinel run id for the optimistic upload working state (OR-6125), so the
// real RUN_STARTED can supersede it and ``endUpload`` only clears the placeholder.
const OPTIMISTIC_UPLOAD_RUN_ID = 'optimistic-upload';

// OR-6247 A2: sentinel run id for the optimistic create→resume working state.
// Creating a concept group from the preview page is a REST turn the BE resumes
// server-side (no socket user_message), so without this the chat sits idle until
// the resume's RUN_STARTED round-trips. Same contract as the upload sentinel: the
// real RUN_STARTED supersedes it, ``endResume`` only clears the placeholder, and
// the A3 progress-stall watchdog backstops a resume that never kicks (the
// optimistic state is ``streaming``, so the 45s watchdog re-pulls durable truth).
const OPTIMISTIC_RESUME_RUN_ID = 'optimistic-resume';

export const useEpiChatPage = ({
  enabled = true,
  wireScope,
  chatId,
  resourceResolvers,
}: UseEpiChatPageOptions): UseEpiChatPageResult => {
  const { stateBySession, dispatchTo, actions } = useEpiChatState();
  const activeSessionId = chatId;

  // Live ref so callbacks reading the active session id (the socket handler,
  // etc.) always see the latest value without re-binding.
  const activeSessionIdRef = useRef(activeSessionId);
  activeSessionIdRef.current = activeSessionId;

  const stateBySessionRef = useRef(stateBySession);
  stateBySessionRef.current = stateBySession;

  const state = selectSessionState(stateBySession, activeSessionId);

  // Persist every session's transcript independently (see hook docs).
  useEpiChatPersistence(activeSessionId, stateBySession, dispatchTo);

  const runCompletionNotifier = useRunCompletionNotifier(activeSessionIdRef);
  const { requestPermission: requestNotificationPermission } = runCompletionNotifier;

  const reduceEvent = useEpiChatStream(
    dispatchTo,
    activeSessionIdRef,
    resourceResolvers,
    runCompletionNotifier
  );

  const onEvent = useCallback(
    (event: AgUiEvent) => {
      reduceEvent(event);
    },
    [reduceEvent]
  );

  const { status, send } = useEpiStudyAgentSocket({ onEvent, enabled });
  // UI-facing status: brief auto-reconnect blips are smoothed so the composer
  // and pending cards don't grey out on a self-healing drop. The raw ``status``
  // below still drives subscribe/replay, so a real reconnect re-subscribes.
  const connectionStatus = useGracefulConnectionStatus(status);

  // Live ref so the scope captured by sendMessage / scope_changed / the
  // queue-flush effect is always the latest one without re-binding every
  // callback when the URL changes.
  const wireScopeRef = useRef<WireScope | undefined>(wireScope);
  wireScopeRef.current = wireScope;

  // OR-6021: parallel ref for the soft user_context value. Derived from
  // the same URL-source as ``wireScope`` but with draft kinds filtered
  // out (a draft has no URL the user can be on). ``useMemo`` keyed on
  // the wireScope ref keeps a stable identity per scope-change so the
  // ``user_context_changed`` effect can dedupe by reference (mirroring
  // how ``scope_changed`` dedupes on ``wireScope`` itself) — without
  // the memo every render would re-parse through Zod and produce a
  // fresh object, defeating the dedupe.
  const wireUserContext: WireUserContext | undefined = useMemo(
    () => toWireUserContext(wireScope),
    [wireScope]
  );
  const wireUserContextRef = useRef<WireUserContext | undefined>(wireUserContext);
  wireUserContextRef.current = wireUserContext;
  const lastSentUserContextRef = useRef<WireUserContext | undefined>(undefined);

  // Live ref so the subscribe effect always reads the latest chatId
  // without re-binding when the URL changes mid-session. Project-rooted
  // S3 dispatch on the BE keys off this; without the ref we could end up
  // subscribing the new chat under the previous chat's session_id.
  const chatIdRef = useRef<string | undefined>(chatId);
  chatIdRef.current = chatId;

  // Tracks the scope last carried on the wire (either by `subscribe` at
  // session start / reconnect or by a prior `scope_changed` emit). Lets
  // the scope-change effect dedupe identical refs.
  const lastSentScopeRef = useRef<WireScope | undefined>(undefined);
  // Flag flipped true on the first subscribe of a session: until then,
  // the scope-change effect must stay quiet (subscribe carries the
  // initial scope; emitting before subscribe would arrive ahead of any
  // session on the server). Reset on session-switch so a fresh session
  // also waits for its first subscribe.
  const subscribeFiredRef = useRef(false);

  // Subscribe whenever:
  //   (a) the socket is (re)connected, OR
  //   (b) the active session id changes (the URL-derived chatId prop swaps).
  const lastSubscribedRef = useRef<{ status: EpiSocketStatus; sessionId: string | null }>({
    status: 'disconnected',
    sessionId: null,
  });
  // Holds off subscribing while this exact chat id's create-POST is still in
  // flight. The FE mints the chat id and navigates before that POST resolves
  // (``ChatPanelEmptyState.mintChat``), so without this the subscribe below
  // can reach the BE before the row commits — the BE's own lookup retry
  // there is bounded to ~1.5s and real creation latency can exceed it,
  // surfacing a terminal "You don't have access to this chat." for a chat
  // that's perfectly valid moments later. `false` for an existing chat with
  // no in-flight create (the common reopen case), so this never delays
  // anything but the exact race window.
  const pendingChatCreation = usePendingChatCreation(activeSessionId);
  useEffect(() => {
    if (status !== 'connected') {
      lastSubscribedRef.current = { status, sessionId: lastSubscribedRef.current.sessionId };
      return;
    }
    if (!activeSessionId) return;
    const last = lastSubscribedRef.current;
    const isReconnect = last.status !== 'connected';
    const isSessionSwitch = last.sessionId !== activeSessionId;
    if (!isReconnect && !isSessionSwitch) return;
    // Don't update `lastSubscribedRef` here — once the create settles and
    // `pendingChatCreation` flips to false, `isSessionSwitch`/`isReconnect`
    // are still true above, so this effect re-runs and subscribes normally.
    if (pendingChatCreation) return;

    // Pass the resume cursor so the backend's per-session ring buffer can
    // replay any events that streamed while the socket was gone (refresh,
    // brief drop, session swap). Missing cursor (0) = first subscribe, no
    // replay needed.
    const resumeCursor = stateBySessionRef.current[activeSessionId]?.lastEventSeq ?? 0;
    const subscribeScope = wireScopeRef.current;
    const subscribeUserContext = wireUserContextRef.current;
    const subscribeChatId = chatIdRef.current;
    send('subscribe', {
      session_id: activeSessionId,
      since_seq: resumeCursor,
      ...(subscribeScope ? { scope: subscribeScope } : {}),
      // OR-6021: soft user_context — the URL-derived artifact the user
      // is currently looking at, minus draft kinds. Server stores it on
      // the in-memory session record and re-stamps a ``<user_context>``
      // system-prompt block on the next turn.
      ...(subscribeUserContext ? { user_context: subscribeUserContext } : {}),
      // ``chat_id`` (snake_case wire, matches the BE ``on_subscribe``
      // schema) drives EPI_CHAT_SESSION resolution + project-rooted S3
      // layout dispatch.
      ...(subscribeChatId ? { chat_id: subscribeChatId } : {}),
    });
    // Subscribe carries the floor scope; the scope-change effect won't
    // emit a redundant ``scope_changed`` until the URL produces a new
    // ref. Tracking it here means a reconnect / session-switch re-syncs
    // the server's view to whatever the URL says right now.
    lastSentScopeRef.current = subscribeScope;
    lastSentUserContextRef.current = subscribeUserContext;
    subscribeFiredRef.current = true;
    dispatchTo(activeSessionId, actions.sessionEstablished({ sessionId: activeSessionId }));
    dispatchTo(activeSessionId, actions.subscribeStarted());
    lastSubscribedRef.current = { status, sessionId: activeSessionId };
  }, [status, activeSessionId, pendingChatCreation, send, dispatchTo, actions]);

  // Auto-retry a *transient* subscribe refusal (``SUBSCRIBE_UNAVAILABLE``) with
  // bounded backoff before dead-ending on the terminal "no access" panel. Each
  // refusal bumps ``subscribeRetryNonce``; a successful ack (``subscribeAcked``)
  // resets the attempt budget. A persistently-throwing read still ends on the
  // panel once the budget is exhausted.
  const subscribeAttemptsRef = useRef(0);
  useEffect(() => {
    if (state.subscribeAcked) subscribeAttemptsRef.current = 0;
  }, [state.subscribeAcked]);
  useEffect(() => {
    if (!state.subscribeRetryNonce) return;
    if (status !== 'connected' || !activeSessionId) return;
    const attempt = subscribeAttemptsRef.current;
    if (attempt >= SUBSCRIBE_RETRY_BACKOFF_MS.length) {
      dispatchTo(
        activeSessionId,
        actions.runErrored({
          message: "Couldn't connect to this chat — please reload to try again.",
        })
      );
      return;
    }
    subscribeAttemptsRef.current = attempt + 1;
    const timer = setTimeout(() => {
      const resumeCursor = stateBySessionRef.current[activeSessionId]?.lastEventSeq ?? 0;
      const subscribeScope = wireScopeRef.current;
      const subscribeUserContext = wireUserContextRef.current;
      const subscribeChatId = chatIdRef.current;
      send('subscribe', {
        session_id: activeSessionId,
        since_seq: resumeCursor,
        ...(subscribeScope ? { scope: subscribeScope } : {}),
        ...(subscribeUserContext ? { user_context: subscribeUserContext } : {}),
        ...(subscribeChatId ? { chat_id: subscribeChatId } : {}),
      });
      dispatchTo(activeSessionId, actions.subscribeStarted());
    }, SUBSCRIBE_RETRY_BACKOFF_MS[attempt]);
    return () => clearTimeout(timer);
  }, [state.subscribeRetryNonce, status, activeSessionId, send, dispatchTo, actions]);

  // OR-6247 A3: progress-stall watchdog. While a run is streaming, arm a timer
  // for ``RUN_PROGRESS_STALL_MS``; every event that advances ``lastEventSeq``
  // re-runs this effect and re-arms a fresh timer, so continuous progress never
  // fires it. Only a genuine stall (no event for the whole window) re-subscribes
  // from the durable cursor — pulling any progress produced on another pod that
  // never reached this socket. The re-subscribe is SILENT: it does NOT dispatch
  // ``subscribeStarted`` (which would clear ``subscribeAcked`` and wedge the
  // composer); the BE replays only events after the cursor (incremental, no
  // duplicates, A5) and the ready-ack reconciles ``runState`` to durable truth
  // (so a run that actually finished resolves instead of spinning — A4/A7).
  useEffect(() => {
    if (status !== 'connected' || !activeSessionId) return;
    if (state.runState !== 'streaming') return;
    const timer = setTimeout(() => {
      const resumeCursor = stateBySessionRef.current[activeSessionId]?.lastEventSeq ?? 0;
      const subscribeScope = wireScopeRef.current;
      const subscribeUserContext = wireUserContextRef.current;
      const subscribeChatId = chatIdRef.current;
      send('subscribe', {
        session_id: activeSessionId,
        since_seq: resumeCursor,
        ...(subscribeScope ? { scope: subscribeScope } : {}),
        ...(subscribeUserContext ? { user_context: subscribeUserContext } : {}),
        ...(subscribeChatId ? { chat_id: subscribeChatId } : {}),
      });
    }, RUN_PROGRESS_STALL_MS);
    return () => clearTimeout(timer);
  }, [status, activeSessionId, state.runState, state.lastEventSeq, send]);

  // Emit ``scope_changed`` whenever the URL-derived scope ref-changes
  // *after* the initial subscribe. The server uses this to update the
  // session manifest's ``focusScope`` so the persisted view stays in
  // sync without waiting for the next ``user_message``. ACK-only on the
  // server — no agent run is triggered.
  useEffect(() => {
    if (status !== 'connected') return;
    if (!activeSessionId) return;
    if (!wireScope) return;
    if (!subscribeFiredRef.current) return;
    if (wireScope === lastSentScopeRef.current) return;
    send('scope_changed', { scope: wireScope });
    lastSentScopeRef.current = wireScope;
  }, [wireScope, status, activeSessionId, send]);

  // OR-6021: emit ``user_context_changed`` on the same trigger but
  // through the new soft-signal channel. Independent of
  // ``scope_changed`` so the two can diverge as the legacy scope rail
  // is dismantled. Skips emit when the URL produced no user_context
  // (e.g. a draft scope from the agent) so we don't blast nulls.
  useEffect(() => {
    if (status !== 'connected') return;
    if (!activeSessionId) return;
    if (!wireUserContext) return;
    if (!subscribeFiredRef.current) return;
    if (wireUserContext === lastSentUserContextRef.current) return;
    send('user_context_changed', { user_context: wireUserContext });
    lastSentUserContextRef.current = wireUserContext;
  }, [wireUserContext, status, activeSessionId, send]);

  const sendMessage = useCallback(
    (payload: EpiChatSendPayload) => {
      if (!activeSessionId) return;
      // Sending is a user gesture, the only point a browser honours the
      // notification permission prompt for the run-completion alert.
      requestNotificationPermission();
      // Queue instead of emit when a run is streaming OR the socket is down.
      // The disconnect case keeps a retry usable while the socket reconnects
      // (a run ended but a transient drop left the composer unable to send —
      // the OR-6265/Tim "progress stopped, can't retry" symptom): the message
      // lands in the visible queue and the queue-flush effect drains it once
      // ``idle`` AND reconnected, rather than being lost to a dead socket.
      if (state.runState === 'streaming' || status !== 'connected') {
        dispatchTo(activeSessionId, actions.messageQueued(payload));
        return;
      }
      // Client-minted id shared between the optimistic bubble and the
      // ``message_id`` on the wire. The server echoes it back to the session
      // room so other tabs render the same bubble live; this tab dedupes on it.
      // A caller may already have rendered this message optimistically under
      // a pre-minted id (the new-chat seed) — reuse it instead of minting a
      // second id, so ``userMessageSent``'s dedupe guard treats this as the
      // same message rather than a duplicate.
      const messageId = payload.id ?? nanoid();
      dispatchTo(
        activeSessionId,
        actions.userMessageSent({
          id: messageId,
          text: payload.text,
          questionId: payload.questionId,
          checkpointId: payload.checkpointId,
          attachments: payload.attachments,
        })
      );
      // OR-6348: flip to "streaming" immediately so "RA is working" appears
      // instantly instead of waiting on the real RUN_STARTED round-trip —
      // same sentinel contract as beginUpload/beginResume above; the real
      // event supersedes it a beat later. Side effect: it makes this
      // callback's own streaming guard (above) catch a rapid same-tab
      // follow-up send and queue it locally instead of re-emitting.
      dispatchTo(activeSessionId, actions.runStarted({ runId: OPTIMISTIC_SEND_RUN_ID }));
      const messageScope = wireScopeRef.current;
      const messageUserContext = wireUserContextRef.current;
      send('user_message', {
        message_id: messageId,
        text: payload.text,
        question_id: payload.questionId,
        // OR-6262: the checkpoint this reply resolves. The server echoes it back
        // on the durable ``user_message`` ui# event so a reload / second tab can
        // fold it and render the checkpoint card resolved (chip + answer, no
        // buttons) instead of re-opening the already-clicked dialog.
        checkpoint_id: payload.checkpointId,
        choice: payload.choice,
        // References only — the bytes are already stored.
        ...(payload.attachments?.length
          ? { attachments: payload.attachments.map(toWireAttachment) }
          : {}),
        ...(messageScope ? { scope: messageScope } : {}),
        // OR-6021: soft user_context per-turn; mirrors the subscribe
        // payload's shape. Server uses it to re-stamp the orchestrator's
        // <user_context> system-prompt block before the next agent turn.
        ...(messageUserContext ? { user_context: messageUserContext } : {}),
      });
    },
    [
      state.runState,
      status,
      dispatchTo,
      actions,
      send,
      activeSessionId,
      requestNotificationPermission,
    ]
  );

  const cancelRun = useCallback(() => {
    if (state.runState !== 'streaming') return;
    if (!activeSessionId) return;
    // Optimistic local cancel: flip runState to 'idle' and close pending
    // surfaces NOW so the composer re-enables immediately and the user
    // can move on. The server-side cancel takes a beat to land (the agent
    // task has to receive the cancel + finally-block out + emit the
    // STATUS_UPDATE cancelled event), and waiting on that round-trip
    // makes the Stop button feel broken. The reducer's `runCancelled`
    // is idempotent — when the server's STATUS_UPDATE arrives, hitting
    // it a second time is a no-op.
    dispatchTo(activeSessionId, actions.runCancelled());
    send('cancel', { run_id: state.runId });
  }, [state.runState, state.runId, send, activeSessionId, dispatchTo, actions]);

  // OR-6125: optimistic working state for a file upload. The upload is a REST
  // turn (no socket user_message), so without this the user picks a file and
  // sees nothing until the run's RUN_STARTED round-trips. Flip to streaming the
  // instant the file is picked; the real run takes over a beat later (and
  // RUN_FINISHED clears it). ``endUpload`` is the safety net for a POST that
  // never starts a turn (network error) — it only clears the optimistic
  // placeholder, never a real run that already took over.
  const beginUpload = useCallback(() => {
    if (!activeSessionId || state.runState === 'streaming') return;
    dispatchTo(activeSessionId, actions.runStarted({ runId: OPTIMISTIC_UPLOAD_RUN_ID }));
  }, [activeSessionId, state.runState, dispatchTo, actions]);

  const endUpload = useCallback(() => {
    if (!activeSessionId || state.runId !== OPTIMISTIC_UPLOAD_RUN_ID) return;
    dispatchTo(activeSessionId, actions.runFinished());
  }, [activeSessionId, state.runId, dispatchTo, actions]);

  // OR-6247 A2: optimistic working state for a create→resume. Creating a concept
  // group from the preview page POSTs to confirm; the BE then resumes the chat
  // server-side, so the chat would otherwise sit idle until that resume's
  // RUN_STARTED round-trips. Flip to streaming the instant Create fires; the real
  // run supersedes it a beat later. ``endResume`` is the safety net for a confirm
  // that rejects before any turn starts — it only clears the placeholder, never a
  // real run that already took over.
  const beginResume = useCallback(() => {
    if (!activeSessionId || state.runState === 'streaming') return;
    dispatchTo(activeSessionId, actions.runStarted({ runId: OPTIMISTIC_RESUME_RUN_ID }));
  }, [activeSessionId, state.runState, dispatchTo, actions]);

  const endResume = useCallback(() => {
    if (!activeSessionId || state.runId !== OPTIMISTIC_RESUME_RUN_ID) return;
    dispatchTo(activeSessionId, actions.runFinished());
  }, [activeSessionId, state.runId, dispatchTo, actions]);

  const retryInterruptedRun = useCallback(() => {
    if (!activeSessionId) return;
    // Walk backward for the last user-typed text and re-send it through the
    // composer path so a fresh run starts. Gate replies (checkpoint /
    // clarifying answers) re-fire as plain text — the carried questionId /
    // checkpointId is intentionally dropped, since the interrupted run that
    // would have consumed them is gone; the new run re-reads the gate from
    // its own state. ``sendMessage`` no-ops into the queue if a run is
    // somehow streaming, but ``runInterrupted`` already flipped runState to
    // ``idle``, so this fires immediately.
    for (let i = state.messages.length - 1; i >= 0; i -= 1) {
      const message = state.messages[i];
      if (message.role !== 'user') continue;
      const textPart = message.parts.find(
        (part): part is { kind: 'text'; text: string } => part.kind === 'text'
      );
      // The stored file is untouched by the interruption, so the references re-send.
      const attachments = message.parts.flatMap((part) =>
        part.kind === 'attachment' ? [part.attachment] : []
      );
      const text = textPart?.text ?? '';
      // Words OR files: keying on text alone would re-send an earlier message.
      if (text.trim().length > 0 || attachments.length > 0) {
        sendMessage({ text, ...(attachments.length ? { attachments } : {}) });
        return;
      }
    }
  }, [activeSessionId, state.messages, sendMessage]);

  const dequeueMessage = useCallback(
    (index: number) => {
      if (!activeSessionId) return;
      dispatchTo(activeSessionId, actions.messageDequeued({ index }));
    },
    [dispatchTo, actions, activeSessionId]
  );

  const selectArtifact = useCallback(
    (id: string | null) => {
      if (!activeSessionId) return;
      dispatchTo(activeSessionId, actions.artifactSelected({ id }));
    },
    [dispatchTo, actions, activeSessionId]
  );

  const selectArtifactTab = useCallback(
    (tab: EpiArtifactTab | null) => {
      if (!activeSessionId) return;
      dispatchTo(activeSessionId, actions.artifactTabSelected({ tab }));
    },
    [dispatchTo, actions, activeSessionId]
  );

  const applyArtifactScope = useCallback(
    (id: string, scope: NonNullable<EpiArtifact['scope']>) => {
      if (!activeSessionId) return;
      dispatchTo(activeSessionId, actions.artifactScopeChanged({ id, scope }));
    },
    [dispatchTo, actions, activeSessionId]
  );

  // Flush the head of the queue when a run finishes AND the socket is up.
  // The connection guard lets a message queued during a disconnect (see
  // ``sendMessage``) drain on reconnect instead of being emitted to a dead
  // socket — so a retry typed while briefly offline sends when the socket
  // returns.
  useEffect(() => {
    if (!activeSessionId) return;
    if (state.runState !== 'idle') return;
    if (status !== 'connected') return;
    if (state.queue.length === 0) return;
    const [next, ...rest] = state.queue;
    dispatchTo(activeSessionId, actions.queueFlushed());
    for (const payload of rest) {
      dispatchTo(activeSessionId, actions.messageQueued(payload));
    }
    const queuedMessageId = next.id ?? nanoid();
    dispatchTo(
      activeSessionId,
      actions.userMessageSent({
        id: queuedMessageId,
        text: next.text,
        questionId: next.questionId,
        checkpointId: next.checkpointId,
        attachments: next.attachments,
      })
    );
    const flushScope = wireScopeRef.current;
    const flushUserContext = wireUserContextRef.current;
    send('user_message', {
      message_id: queuedMessageId,
      text: next.text,
      question_id: next.questionId,
      choice: next.choice,
      ...(next.attachments?.length ? { attachments: next.attachments.map(toWireAttachment) } : {}),
      ...(flushScope ? { scope: flushScope } : {}),
      ...(flushUserContext ? { user_context: flushUserContext } : {}),
    });
  }, [state.runState, status, state.queue, dispatchTo, actions, send, activeSessionId]);

  // OR-6078: mirror the gen-3 agent run state into the shared
  // ``isWaitingForResponse`` signal. The legacy research-assistant slice never
  // sets this in the gen-3 flow, so without this bridge the cohort builder's
  // single-writer lock (which reads ``useIsWaitingForResponse``) can't tell
  // when the agent owns the cohort. ``streaming`` = actively running;
  // ``awaiting_input`` = parked on a checkpoint/clarifying question awaiting the
  // user — both mean the agent, not the user, is the writer. Cleared on unmount
  // so a chat torn down mid-run doesn't leave the lock stuck on.
  useEffect(() => {
    const agentActive = state.runState === 'streaming' || state.runState === 'awaiting_input';
    setIsWaitingForResponse(agentActive);
    return () => setIsWaitingForResponse(false);
  }, [state.runState]);

  const activeArtifact =
    state.artifacts.find((artifact) => artifact.id === state.activeArtifactId) ?? null;

  return {
    // Debounced for UI gating; internal subscribe logic uses the raw ``status``.
    status: connectionStatus,
    messages: state.messages,
    queue: state.queue,
    runState: state.runState,
    error: state.error,
    initializing: state.initializing,
    subscribing: state.subscribing,
    subscribeAcked: state.subscribeAcked,
    artifacts: state.artifacts,
    activeArtifact,
    activeArtifactTab: state.activeArtifactTab,
    activeSessionId,
    sendMessage,
    cancelRun,
    beginUpload,
    endUpload,
    beginResume,
    endResume,
    dequeueMessage,
    selectArtifact,
    selectArtifactTab,
    applyArtifactScope,
    remoteFocusScope: state.remoteFocusScope,
    interruptedRun: state.interruptedRun,
    retryInterruptedRun,
  };
};
